import { HK_IMG } from "./assets";
import {
  CAIRA_COURSE_LEVELS,
  type CairaCourseLevel,
} from "@/components/newPageComponent/new-home/cairaCourses";
import {
  thumbSrc,
  thumbSrcSet,
} from "@/components/newPageComponent/new-home/thumbnails";

export type HackathonTrack = "school" | "college";

export interface TrackConfig {
  track: HackathonTrack;
  siteUrl: string;
  finaleISO: string;
  shareText: string;
  /** Promo message for the share card (sent by someone who may not have registered) */
  inviteText: string;
  futureNote: string;
}

export const COLLEGE_CONFIG: TrackConfig = {
  track: "college",
  siteUrl: "https://www.mileseducation.com/hackathon-ug",
  finaleISO: "2027-01-30",
  shareText:
    "I just registered for India's first hackathon for Commerce & Business students, by Miles Education. It's free for UG students across India, and the first prize is ₹1,00,000. Register here:",
  inviteText:
    "India's first hackathon for Commerce & Business students is here! Build your 1st AI agent for finance workflows in a free live webinar by Miles Education, then compete for ₹1,00,000. Register free:",
  futureNote: "At Miles, you can take CAIRA alongside your U.S. CPA or CMA.",
};

export const SCHOOL_CONFIG: TrackConfig = {
  track: "school",
  siteUrl: "https://www.mileseducation.com/hackathon-school",
  finaleISO: "2027-01-23",
  shareText:
    "I just registered for India's first hackathon for Commerce & Business students, by Miles Education. It's free for Class 11 & 12 Commerce students in Kerala, and the first prize is ₹1,00,000. Register here:",
  inviteText:
    "India's first hackathon for Commerce & Business students is here! Build your 1st AI agent for finance workflows in a free live webinar by Miles Education, then compete for ₹1,00,000. Free for Class 11 & 12 Commerce students in Kerala. Register free:",
  futureNote:
    "U.S. CPA and CMA are taken up during or after college. CAIRA builds the AI skills on top.",
};

export const KERALA_DISTRICTS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
];

export const INDIA_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

export const COLLEGE_COURSES = [
  "B.Com",
  "BBA",
  "BMS",
  "BAF",
  "BBM",
  "Other Commerce or Business UG",
];

/* Metal tones shared by the podium, CAIRA levels and masterclass cards.
   `mix` is the tone blended 18% into #1C1F2B (selected tab background). */
export type Tone = "gold" | "silver" | "bronze" | "steel";
export const TONES: Record<Tone, { hex: string; rgb: string; mix: string }> = {
  gold: { hex: "#FDBA12", rgb: "253,186,18", mix: "#453B27" },
  silver: { hex: "#C3CAD6", rgb: "195,202,214", mix: "#3A3E4A" },
  bronze: { hex: "#CD8645", rgb: "205,134,69", mix: "#3C3230" },
  steel: { hex: "#8A93A6", rgb: "138,147,166", mix: "#33384A" },
};

/**
 * One CAIRA masterclass card. The courses come straight from the homepage
 * (components/newPageComponent/new-home/cairaCourses.ts), so this section and
 * the homepage always list the same masterclasses. The thumbnail already shows
 * the masterclass name and speaker, so the card has no separate title.
 */
export interface Masterclass {
  id: string;
  /** The homepage card text — a one-line summary of the masterclass. */
  description: string;
  category: string;
  /** ImageKit thumbnail (16:9), with a srcset for the right width. */
  image: string;
  imageSrcSet: string;
  videoUrl: string;
}

export interface CairaLevel {
  n: number;
  tone: Tone;
  badge: string;
  name: string;
  sub: string;
  cards: Masterclass[];
}

const toCards = (level: CairaCourseLevel): Masterclass[] =>
  level.courses.map((c) => ({
    id: c.id,
    description: c.title,
    category: c.category,
    image: thumbSrc(level.ik, c.thumbnail, 640),
    imageSrcSet: thumbSrcSet(level.ik, c.thumbnail),
    videoUrl: c.videoUrl,
  }));

/** Badge art and colour are this page's own; names, lines and courses are the homepage's. */
const LEVEL_STYLE: Record<number, { tone: Tone; badge: string }> = {
  1: { tone: "bronze", badge: HK_IMG.badgeL1 },
  2: { tone: "silver", badge: HK_IMG.badgeL2 },
  3: { tone: "gold", badge: HK_IMG.badgeL3 },
};

export const CAIRA_LEVELS: CairaLevel[] = CAIRA_COURSE_LEVELS.map((level) => ({
  n: level.n,
  ...LEVEL_STYLE[level.n]!,
  name: level.title,
  sub: level.sub,
  cards: toCards(level),
}));
